import { useEffect, useRef } from 'react'
import { RoomEvent, Track, type RemoteTrackPublication, type Room } from 'livekit-client'
import { getSocket } from '../../services/socket'
import { useAuthStore } from '../../stores/authStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'
import { useToastStore } from '../../stores/toastStore'
import { authApi, pollApi, presentationApi } from '../../services/api'
import { useLiveEventStore } from '../../stores/liveEventStore'
import { mediaManager, onScreenShareStarted, onScreenShareStopped } from '../../services/mediaManager'
import { closeSharedAudioContext } from './audioContext'
import { sounds } from '../../services/sounds'
import { BACKEND_URL } from '../../services/config'
import { useHotMicMonitor } from '../../hooks/useHotMicMonitor'
import { useDmChannelPrivacyGate } from '../../hooks/useDmChannelPrivacyGate'
import { useRoomPhoneTranscription } from '../../hooks/useRoomPhoneTranscription'
import { SpeakerStickiness } from '../../services/videoFocus'
import { initDmChannelBridge, disposeDmChannelBridge } from '../../services/dmChannelBridge'
import CallTabBlockModal from './CallTabBlockModal'
import ParticipantActionsMenu from './ParticipantActionsMenu'
import ParticipantProfileDrawer from './ParticipantProfileDrawer'
import RemoteControlController from './RemoteControlController'

export default function VoiceManager() {
  // Hot-mic detection: surface a one-shot actionable toast suggesting DFN
  // when the local mic level stays sustained-high for ≥30s. Self-deduping
  // per session; no-op when not in a call (level=0). See useHotMicMonitor.
  useHotMicMonitor()
  // DM↔Channel privacy gate: auto-mute channel mic when DM accepted
  // while in channel (and vice versa), restore on exit. See spec
  // docs/superpowers/specs/2026-05-26-dm-channel-bridge-design.md.
  useDmChannelPrivacyGate()

  // Bridge audio between channel and DM when both are active + both
  // mics on. Initialised once per session — service maintains its own
  // store subscriptions internally.
  useEffect(() => {
    initDmChannelBridge()
    return () => {
      disposeDmChannelBridge()
    }
  }, [])

  const currentUser = useAuthStore((s) => s.user)
  const currentChannelId = useVoiceStore((s) => s.currentChannelId)
  const knownParticipantsRef = useRef<Set<string>>(new Set())

  // OSPROD-184: room-scope PSTN phone-leg transcription feeder — armed
  // whenever we're in a voice channel that has phone participants and we
  // win the feeder election. App-level (same rationale as VoiceManager
  // itself) so the feeder keeps recording while the user navigates away
  // from the room UI mid-call.
  const livekitRoom = useLivekitStore((s) => s.room)
  useRoomPhoneTranscription(currentChannelId, livekitRoom)

  // Load peer customizations from user settings
  useEffect(() => {
    if (currentUser?.settings?.peerCustomizations) {
      useVoiceStore.getState().loadPeerCustomizations(currentUser.settings.peerCustomizations)
    }
  }, [currentUser?.settings?.peerCustomizations])

  // Persist peer customizations to user settings (debounced)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsub = useVoiceStore.subscribe((state, prevState) => {
      const changed =
        JSON.stringify(state.peerVolumes) !== JSON.stringify(prevState.peerVolumes) ||
        JSON.stringify(state.peerVideoHidden) !== JSON.stringify(prevState.peerVideoHidden)
      if (!changed) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        const { peerVolumes, peerVideoHidden } = useVoiceStore.getState()
        const { user } = useAuthStore.getState()
        const currentCustomizations = (user?.settings as any)?.peerCustomizations || {}
        const allIds = new Set([
          ...Object.keys(currentCustomizations),
          ...Object.keys(peerVolumes),
          ...Object.keys(peerVideoHidden),
        ])
        const next: Record<string, any> = {}
        allIds.forEach((id) => {
          next[id] = {
            ...(currentCustomizations[id] || {}),
            volume: peerVolumes[id] ?? currentCustomizations[id]?.volume ?? 1,
            videoHidden: peerVideoHidden[id] ?? currentCustomizations[id]?.videoHidden ?? false,
          }
        })
        if (!user) return
        authApi
          .updateMe({ settings: { ...(user?.settings as any), peerCustomizations: next } })
          .then((res) => {
            useAuthStore.setState({ user: res.data })
          })
          .catch(() => {})
      }, 800)
    })
    return () => {
      unsub()
      if (timer) clearTimeout(timer)
    }
  }, [])

  // OSPROD-55: synchronous "I'm leaving" beacon on pagehide so other
  // clients drop the user from the channel sidebar within milliseconds
  // of a tab close, rather than waiting 25-60s for socket.io's
  // heartbeat to declare the connection dead. fetch with keepalive is
  // used (not navigator.sendBeacon) because the server endpoint
  // authenticates via the standard Bearer header, and sendBeacon
  // can't set custom headers. The server janitor + cron sweeper still
  // cover the case where the beacon itself fails to land (offline at
  // exit, browser process kill before flush).
  useEffect(() => {
    if (!currentChannelId) return
    const fireLeaveBeacon = () => {
      const cid = useVoiceStore.getState().currentChannelId
      if (!cid) return
      const token = localStorage.getItem('token')
      if (!token) return
      try {
        fetch(`${BACKEND_URL}/api/voice/leave-beacon`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ channelId: cid }),
        }).catch(() => undefined)
      } catch {
        // Some browsers reject keepalive on certain network states;
        // the server's reconcile loop is the safety net.
      }
    }
    window.addEventListener('pagehide', fireLeaveBeacon)
    return () => {
      window.removeEventListener('pagehide', fireLeaveBeacon)
    }
  }, [currentChannelId])

  // peerVolumes are applied through the Web Audio GainNode in
  // LiveKitBackgroundAudio (gain.gain.value = peerVolume) — that path
  // is the authoritative volume layer, supports >1× boost, and uses
  // the SHARED AudioContext so a single setSinkId call routes all
  // remotes to the chosen output device.
  //
  // We deliberately do NOT call `participant.setVolume(volume)` here:
  //   1. livekit-client's setVolume sets `el.volume = volume` on every
  //      attached HTMLMediaElement, including the silent keep-alive
  //      we attach in livekitStore on TrackSubscribed. That un-silences
  //      the keep-alive and plays the SAME track through both pipes,
  //      so peers were heard twice with a few-ms delay (big-call QA).
  //   2. HTMLMediaElement.volume must be in [0,1]; the slider permits
  //      up to ~2× boost — setVolume(1.5) threw IndexSizeError and
  //      the toast appeared mid-call.
  // The keep-alive element is now `.muted = true` (see livekitStore /
  // dmCallStore), so even if something else calls setVolume in the
  // future, the element stays silent.

  // Apply peerVideoHidden → SFU-level unsubscribe so the hidden remote
  // doesn't pay for tile rendering AND we stop receiving their video
  // bitrate over the wire. The user only hides for THEMSELVES — peers
  // still see them normally because this is a downlink-side
  // subscription change, not a publish change. Re-run on every store
  // change so toggles flip the subscription live.
  useEffect(() => {
    // "Видео только говорящих" (2026-06-12): recent-speaker stickiness
    // keeps a speaker's tile alive through natural pauses so video
    // doesn't flicker on sentence boundaries. See services/videoFocus.
    const stickiness = new SpeakerStickiness()
    const apply = () => {
      const room = useLivekitStore.getState().room
      if (!room || !room.remoteParticipants) return
      const hidden = useVoiceStore.getState().peerVideoHidden
      const offscreen = useVoiceStore.getState().peerVideoOffscreen
      const speakersOnly = useVoiceStore.getState().videoSpeakersOnly
      const now = Date.now()
      if (speakersOnly) {
        try {
          stickiness.update((room.activeSpeakers ?? []).map((p) => p.identity), now)
        } catch { /* partial mocks in tests */ }
      }
      room.remoteParticipants.forEach((participant) => {
        // Unsubscribe (stop decoding) a remote camera when the user hid it OR
        // when its grid tile scrolled off-screen (peerVideoOffscreen) — the
        // latter stops a big grid from decoding 20+ VP9 streams at once
        // (2026-05-22 camera-flicker fix). Both are downlink-only; peers still
        // publish + others still see them.
        const wantHidden =
          !!hidden[participant.identity]
          || !!offscreen[participant.identity]
          // "Видео только говорящих": with the toggle on, cameras of
          // everyone who hasn't spoken inside the sticky window drop
          // their subscription — receive bandwidth and decode CPU scale
          // with active speakers instead of room size (27-person-call
          // request). Screen share is untouched (Camera source only).
          || (speakersOnly && !stickiness.isRecentSpeaker(participant.identity, now))
        // Camera publication only — screen-share is left alone (the
        // user toggles it via its own button, not the per-tile camera
        // chip). Audio is governed by peerVolumes / muteAll, not here.
        const pub = participant.getTrackPublication(Track.Source.Camera) as
          | RemoteTrackPublication
          | undefined
        if (!pub) return
        const wantSubscribed = !wantHidden
        if (pub.isSubscribed !== wantSubscribed) {
          try { pub.setSubscribed(wantSubscribed) } catch (err) {
            console.warn('[VoiceManager] setSubscribed failed:', err)
          }
        }
      })
      if (speakersOnly) stickiness.prune(now)
    }
    // Initial application — covers reconnects + persisted preferences
    // already loaded before this effect ran.
    apply()
    const unsub = useVoiceStore.subscribe((state, prevState) => {
      if (
        state.peerVideoHidden === prevState.peerVideoHidden &&
        state.peerVideoOffscreen === prevState.peerVideoOffscreen &&
        state.videoSpeakersOnly === prevState.videoSpeakersOnly
      ) return
      apply()
    })
    // New remote tracks land via the room's TrackSubscribed event
    // (livekitStore re-applies the incoming-quality cap there; our
    // subscription decision sits one level higher — whether to receive
    // at all). ActiveSpeakersChanged drives the speakers-only filter.
    // Bind on the CURRENT room and REBIND when livekitStore swaps it —
    // the previous mount-time-only bind silently missed rooms connected
    // after this component mounted.
    let boundRoom: Room | null = null
    const onRoomEvent = () => apply()
    const bind = (room: Room | null) => {
      if (room === boundRoom) return
      if (boundRoom && typeof boundRoom.off === 'function') {
        boundRoom.off(RoomEvent.TrackSubscribed, onRoomEvent)
        boundRoom.off(RoomEvent.ActiveSpeakersChanged, onRoomEvent)
      }
      boundRoom = room
      if (room && typeof room.on === 'function') {
        room.on(RoomEvent.TrackSubscribed, onRoomEvent)
        room.on(RoomEvent.ActiveSpeakersChanged, onRoomEvent)
      }
      stickiness.clear()
    }
    bind(useLivekitStore.getState().room as Room | null)
    const unsubLk = typeof useLivekitStore.subscribe === 'function'
      ? useLivekitStore.subscribe((s, p) => { if (s.room !== p.room) bind(s.room as Room | null) })
      : null
    // Sticky windows EXPIRE without any room event firing — a 2s sweep
    // turns a silent speaker's video off once their window lapses.
    // No-op while the toggle is off.
    const sweep = window.setInterval(() => {
      if (useVoiceStore.getState().videoSpeakersOnly) apply()
    }, 2_000)
    return () => {
      unsub()
      if (unsubLk) unsubLk()
      window.clearInterval(sweep)
      bind(null)
    }
  }, [])

  // Core voice socket listeners (presence only — media is handled by LiveKit)
  const handlersRef = useRef<{
    onStateChanged?: (state: any) => void
    onParticipants?: (data: any[]) => void
    onConnect?: () => void
  }>({})
  // OSPROD-55: timestamp captured the moment we EMIT voice:getParticipants,
  // used as the "as-of" instant when the matching voice:participants
  // response arrives. Snapshots older than any local delta we've seen
  // are merge-applied (not wholesale-replaced) so a slow snapshot
  // can't wipe out a join that landed during the round-trip.
  const participantsFetchStartedAtRef = useRef<number>(0)
  // Presence self-heal throttle — see onParticipants.
  const selfHealLastAtRef = useRef<number>(0)

  // Local screen-share start/stop → system sound. mediaManager emits these
  // for the local publish; one subscription here avoids double-firing with
  // VoiceChannelControls' annotation-overlay subscription.
  useEffect(() => {
    const offStart = onScreenShareStarted(() => sounds.screenShareStart())
    const offStop = onScreenShareStopped(() => sounds.screenShareStop())
    return () => { offStart(); offStop() }
  }, [])

  useEffect(() => {
    const socket = getSocket()

    const onStateChanged = (state: any) => {
      // [movedbg] temporary diagnostic — strip after the move-roster bug is
      // root-caused. Logs every voice:state:changed the client receives.
      const _cur = useVoiceStore.getState().currentChannelId
      console.warn(`[movedbg] recv uid=${state?.userId} ch=${state?.channelId} ${state?.left ? 'LEFT' : 'join'} cur=${_cur} match=${state?.channelId === _cur}`)
      if (state.channelId !== useVoiceStore.getState().currentChannelId) return
      if (state.left) {
        knownParticipantsRef.current.delete(state.userId)
        useVoiceStore.getState().removeParticipant(state.userId)
      } else {
        const isNew = !knownParticipantsRef.current.has(state.userId)
        const isSelf = state.userId === currentUser?.id
        if (isNew && !isSelf) {
          sounds.knock()
        }
        knownParticipantsRef.current.add(state.userId)
        useVoiceStore.setState((prev) => ({
          participants: prev.participants
            .filter((p) => p.userId !== state.userId)
            .concat(state),
          // OSPROD-55: stamp the local-update timestamp so a snapshot
          // fetched BEFORE this delta arrived can't overwrite the join.
          participantsLastUpdate: { ...prev.participantsLastUpdate, [state.userId]: Date.now() },
        }))
        // [movedbg] resulting roster for the current channel
        console.warn(`[movedbg] participants[${_cur}] = ${useVoiceStore.getState().participants.filter((p) => p.channelId === _cur).map((p) => p.userId).join(',')}`)
      }
    }

    const onParticipants = (data: any[]) => {
      // OSPROD-55: if we fired the request, use the captured fetch-start
      // timestamp; otherwise (server-pushed unsolicited list) treat
      // "now" as fetchedAt — equivalent to a fresh snapshot.
      const fetchedAt = participantsFetchStartedAtRef.current || Date.now()
      participantsFetchStartedAtRef.current = 0
      useVoiceStore.getState().applyParticipantsSnapshot(data.map((d) => ({
        id: d.id || `${d.userId}-${d.channelId}`,
        userId: d.userId,
        channelId: d.channelId,
        isMuted: d.isMuted,
        isDeafened: d.isDeafened,
        isSpeaking: d.isSpeaking,
        isVideoOn: d.isVideoOn,
        isScreenShare: d.isScreenShare,
        forceMuted: d.forceMuted,
        joinedAt: d.joinedAt,
        user: d.user,
        // OSPROD-81: client app label ("Windows App", "Web / Chrome").
        // Snapshot mapper explicitly picks fields, so we have to forward
        // this one — the server attaches it from its in-memory map
        // (see attachClientInfo in apps/server/src/socket.ts).
        clientInfo: d.clientInfo ?? null,
      })), fetchedAt)
      // Re-prime the dedupe set so subsequent voice:state:changed events
      // don't fire join sounds for users already in the snapshot.
      const ids = new Set(data.map((d) => d.userId))
      knownParticipantsRef.current = ids

      // Presence self-heal (2026-06-12 big-call incident): the server-side
      // VoiceState can vanish while this client's socket AND LiveKit room
      // stay alive (mass-leave wave wiped 24 of 27 rows mid-call; ghost-
      // killer false positives do it too). The server can't restore what
      // it no longer knows about, and everyone else renders this user as
      // a placeholder tile — perceived as "у всех пропадает видео". The
      // client is the only side that knows it's still in the room: if a
      // snapshot of MY channel doesn't contain ME while my LiveKit room
      // is live, re-register. Throttled so a genuinely-rejected join
      // (ban, takeover) can't loop.
      const cid = useVoiceStore.getState().currentChannelId
      const myId = useAuthStore.getState().user?.id
      const forMyChannel = data.length === 0 || data.some((d) => d.channelId === cid)
      if (cid && myId && forMyChannel && !data.some((d) => d.userId === myId)) {
        const room = useLivekitStore.getState().room
        const now = Date.now()
        if (room && now - selfHealLastAtRef.current > 10_000) {
          selfHealLastAtRef.current = now
          console.warn('[VoiceManager] presence self-heal: my VoiceState missing from snapshot — re-joining', { cid })
          socket.emit('voice:join', { channelId: cid, userId: myId })
          participantsFetchStartedAtRef.current = Date.now()
          socket.emit('voice:getParticipants', { channelId: cid })
        }
      }
    }

    const onMutedByAdmin = (d: { channelId: string }) => {
      if (d?.channelId !== useVoiceStore.getState().currentChannelId) return
      const room = useLivekitStore.getState().room
      if (room) mediaManager.setMicrophoneEnabled(room, false).catch(() => {})
      useAuthStore.getState().muteForJoin()
      getSocket().emit('voice:state:update', { channelId: d.channelId, isMuted: true })
    }

    const onForceMuteChanged = (d: { channelId: string; locked: boolean }) => {
      if (!d?.channelId) return
      useVoiceStore.getState().setForceMutedHere(d.channelId, d.locked)
      if (d.locked && d.channelId === useVoiceStore.getState().currentChannelId) {
        const room = useLivekitStore.getState().room
        if (room) mediaManager.setMicrophoneEnabled(room, false).catch(() => {})
        useAuthStore.getState().muteForJoin()
        getSocket().emit('voice:state:update', { channelId: d.channelId, isMuted: true })
      }
    }

    const onActivity = (e: any) => {
      if (!e || typeof e.type !== 'string') return
      useVoiceStore.getState().addActivityEvent(e)
    }
    const onActivityList = (data: { channelId: string; events: any[] }) => {
      if (!data || data.channelId !== useVoiceStore.getState().currentChannelId) return
      useVoiceStore.getState().setSessionActivity(data.events || [])
    }

    // Rejoin voice room on reconnect so we re-enter the socket room.
    // Also covers the "I reloaded mid-call and my video tiles were
    // empty" case: if the socket reconnects but the LiveKit room
    // never came back (the initial connect attempt below failed
    // before /livekit/token resolved, or threw on a stale token),
    // try connecting again now that auth + sockets are stable.
    const onConnect = () => {
      const cid = useVoiceStore.getState().currentChannelId
      if (!cid) return

      socket.emit('voice:join', { channelId: cid })
      participantsFetchStartedAtRef.current = Date.now()
      socket.emit('voice:getParticipants', { channelId: cid })
      socket.emit('voice:getActivity', { channelId: cid })

      const room = useLivekitStore.getState().room
      const u = useAuthStore.getState().user
      if (room) {
        // Re-verify LiveKit mic state after reconnect
        const isMuted = u?.settings?.isMuted ?? false
        const isDeafened = u?.settings?.isDeafened ?? false
        const forceMutedHere = useVoiceStore.getState().isForceMutedHere(cid)
        const shouldEnable = !isMuted && !isDeafened && !forceMutedHere
        // Invariant #78: a backend-socket blip says nothing about LiveKit media.
        // When the mic already matches intent (enabled + live track, or off),
        // leave it alone — enabling an already-published mic unpublishes and
        // republishes it, a 1-3 s hole on every reconnect (prod 2026-10-16:
        // Firefox user whose socket dropped every ~63 s lost his mic each
        // minute). Only a real desync is re-applied here. Kill-switch:
        // localStorage 'voiceReconnectMicIdempotent'='off'.
        const idempotent =
          typeof localStorage === 'undefined' || localStorage.getItem('voiceReconnectMicIdempotent') !== 'off'
        const micAlreadyInState = shouldEnable
          ? mediaManager.verifyGroundTruth(room, 'audio', true)
          : !room.localParticipant.isMicrophoneEnabled
        if (idempotent && micAlreadyInState) {
          console.info('[VoiceManager] socket reconnect: mic already matches intent — not re-applying', { shouldEnable })
          useLivekitStore.setState({ isAudioTrackReady: shouldEnable })
        } else {
          mediaManager.setMicrophoneEnabled(room, shouldEnable).then((actual) => {
            useLivekitStore.setState({ isAudioTrackReady: actual })
          }).catch(() => {})
        }
        // OSPROD-92: same idea for the camera. The room here is alive,
        // so LiveKit's own internal reconnect typically already
        // republished the camera track for short blips — guard with
        // `!isCameraEnabled` so we don't double-publish. For the case
        // where LiveKit's republish missed (longer drop, signal-only
        // reconnect), this brings the camera back in line with intent.
        const shouldEnableCam = useVoiceStore.getState().lastCameraEnabled
        if (shouldEnableCam && !room.localParticipant.isCameraEnabled) {
          mediaManager.setCameraEnabled(room, true).then((actual) => {
            useLivekitStore.setState({ isVideoTrackReady: actual })
            if (actual) {
              getSocket().emit('voice:state:update', { channelId: cid, isVideoOn: true })
            }
          }).catch((err) => {
            console.warn('[VoiceManager] camera restore on socket reconnect failed:', err)
          })
        }
      } else if (u?.id) {
        // Room never came back — retry the LiveKit connect. Skip if a
        // connect attempt is already in flight (livekitStore guards
        // its own re-entry).
        const isConnecting = useLivekitStore.getState().isConnecting
        if (isConnecting) return
        const { audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
        const username = u.username || u.id || 'User'
        useLivekitStore.getState().connect(cid, username, audioInputDeviceId, isNoiseSuppressionOn, { silentIfBlocked: true })
          .then(() => {
            socket.emit('voice:join', { channelId: cid, userId: u.id })
            participantsFetchStartedAtRef.current = Date.now()
            socket.emit('voice:getParticipants', { channelId: cid })
          })
          .catch((err) => console.warn('voice retry on socket connect failed:', err))
      }
    }

    handlersRef.current = { onStateChanged, onParticipants, onConnect }

    // --- Live events (polls + synchronized presentations) ---------------
    const onPollStarted = (full: any) => {
      if (full?.run?.channelId !== useVoiceStore.getState().currentChannelId) return
      useLiveEventStore.getState().setPoll(full)
    }
    const onPollQuestionChanged = (full: any) => {
      if (full?.run?.channelId !== useVoiceStore.getState().currentChannelId) return
      useLiveEventStore.getState().setPoll(full)
    }
    const onPollTally = (data: any) => {
      useLiveEventStore.getState().applyTally(data.runId, data.questionId, data.tally)
    }
    const onPollEnded = () => {
      useLiveEventStore.getState().setPoll(null)
    }
    const onPresStarted = (full: any) => {
      if (full?.run?.channelId !== useVoiceStore.getState().currentChannelId) return
      useLiveEventStore.getState().setPresentation(full)
    }
    const onPresSlide = (d: any) => {
      useLiveEventStore.getState().setSlideIndex(d.runId, d.index)
    }
    const onPresEnded = () => {
      useLiveEventStore.getState().setPresentation(null)
    }

    // Sticky ownership (2026-07-07): this device just lost voice because the
    // user moved it to another device (an explicit "Move here" hand-off on
    // the other client sent takeover:true; the server evicted us + fanned
    // this event to our other sockets). Drop our LiveKit connection, surface
    // the view-only banner, and toast so the user understands why audio went
    // silent here.
    const onDisconnectedElsewhere = (data: { channelId: string }) => {
      if (!data?.channelId) return
      useLivekitStore.getState().disconnect().catch(() => {})
      useVoiceStore.getState().setVoiceActiveElsewhere({
        channelId: data.channelId,
        deviceLabel: 'another device',
      })
      useToastStore.getState().showToast({
        id: `voice-moved-${data.channelId}`,
        type: 'message',
        title: 'Voice moved to another device',
      })
    }

    socket.on('voice:state:changed', onStateChanged)
    socket.on('connect', onConnect)
    socket.on('voice:muted-by-admin', onMutedByAdmin)
    socket.on('voice:force-mute:changed', onForceMuteChanged)
    socket.on('voice:activity', onActivity)
    socket.on('voice:activity:list', onActivityList)
    socket.on('voice:poll:started', onPollStarted)
    socket.on('voice:poll:question-changed', onPollQuestionChanged)
    socket.on('voice:poll:tally', onPollTally)
    socket.on('voice:poll:ended', onPollEnded)
    socket.on('voice:presentation:started', onPresStarted)
    socket.on('voice:presentation:slide', onPresSlide)
    socket.on('voice:presentation:ended', onPresEnded)
    socket.on('voice:disconnected-elsewhere', onDisconnectedElsewhere)

    // Auto-reconnect to LiveKit on page load if we were in a voice channel.
    // Retries with exponential backoff because the first attempt fires
    // immediately on mount, before the auth token is fully validated /
    // socket has connected. Without retries the user lands in the
    // channel UI but no LiveKit tracks subscribe — they'd have to
    // reload the page a second time. We cap at 4 attempts (~22s of
    // total wait) to stay user-perceptible without spamming.
    const cid = useVoiceStore.getState().currentChannelId
    let cancelled = false
    if (cid && currentUser?.id) {
      const { audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
      const username = currentUser?.username || currentUser?.id || 'User'
      const tryConnect = async (attempt: number): Promise<void> => {
        if (cancelled) return
        // Bail if another path already brought the room up (e.g. the
        // socket on-connect handler beat us to it).
        if (useLivekitStore.getState().room) return
        if (useLivekitStore.getState().isConnecting) return
        try {
          await useLivekitStore.getState().connect(cid, username, audioInputDeviceId, isNoiseSuppressionOn, { silentIfBlocked: true })
          // Sticky ownership (2026-07-07): page-load restore is NO LONGER a
          // takeover. The connect() pre-check refuses to touch LiveKit if
          // another device owns voice (setting voiceActiveElsewhere → banner),
          // so if connect() bailed the room is null — we must NOT emit a join.
          // If connect() succeeded we truly hold voice, so a plain
          // (non-takeover) join is correct. Legacy "newest device wins" is
          // restored only when the kill-switch is off.
          const sticky = (() => { try { return localStorage.getItem('voiceStickyOwnership') !== 'false' } catch { return true } })()
          if (useLivekitStore.getState().room) {
            socket.emit('voice:join', { channelId: cid, userId: currentUser.id, takeover: !sticky })
            participantsFetchStartedAtRef.current = Date.now()
            socket.emit('voice:getParticipants', { channelId: cid })
          }
        } catch (err) {
          if (cancelled) return
          if (attempt >= 3) {
            console.error('Auto-reconnect to voice gave up after retries:', err)
            return
          }
          const delay = Math.min(2000 * 2 ** attempt, 12000) // 2s, 4s, 8s, 12s
          setTimeout(() => { void tryConnect(attempt + 1) }, delay)
        }
      }
      void tryConnect(0)
    }

    return () => {
      cancelled = true
      socket.off('voice:state:changed', onStateChanged)
      socket.off('connect', onConnect)
      socket.off('voice:muted-by-admin', onMutedByAdmin)
      socket.off('voice:force-mute:changed', onForceMuteChanged)
      socket.off('voice:activity', onActivity)
      socket.off('voice:activity:list', onActivityList)
      socket.off('voice:poll:started', onPollStarted)
      socket.off('voice:poll:question-changed', onPollQuestionChanged)
      socket.off('voice:poll:tally', onPollTally)
      socket.off('voice:poll:ended', onPollEnded)
      socket.off('voice:presentation:started', onPresStarted)
      socket.off('voice:presentation:slide', onPresSlide)
      socket.off('voice:presentation:ended', onPresEnded)
      socket.off('voice:disconnected-elsewhere', onDisconnectedElsewhere)
    }
  }, [currentUser?.id])

  // OSPROD-288: rejoin watcher. The page-load ladder above only runs on
  // mount, so a mid-session SFU drop had NO path back — the user sat in a
  // channel with dead media until they manually left and re-entered (prod:
  // 17 zombie sessions across 13 users in one week, averaging two hours).
  // Now that livekitStore honestly clears `room` on an unexpected
  // disconnect, "I intend to be in a channel but hold no room" is an
  // observable state, and this brings the whole join back — LiveKit connect
  // AND `voice:join`, because reconnecting media alone would leave the user
  // out of the socket room and silently break the transcript and roster
  // (that is OSPROD-267's failure mode). Bounded to 3 attempts so a
  // genuinely unreachable SFU can't turn into a reconnect storm.
  // Kill-switch: localStorage voiceAutoRejoinOnDrop='off'.
  useEffect(() => {
    if (!currentUser?.id) return
    const enabled = (() => {
      try { return localStorage.getItem('voiceAutoRejoinOnDrop') !== 'off' } catch { return true }
    })()
    if (!enabled) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    let inFlight = false
    // The ladder only re-arms after a room SURVIVES for a minute. Resetting
    // `attempts` the moment `room`/`isConnecting` existed let a dying
    // connection re-arm it every cycle — the 2026-08-06 reconnect storm:
    // one client rejoined 171×/day in ~2s loops because each brief connect
    // cleared the counter, so "bounded to 3 attempts" never bounded anything.
    let roomUpSince = 0
    const STABLE_MS = 60_000

    const rejoin = async (cid: string) => {
      if (cancelled) return
      const lk = useLivekitStore.getState()
      // Someone else already restored it (socket on-connect, a manual
      // rejoin, a channel switch) — stand down; check() re-arms the ladder
      // once the room proves stable.
      if (lk.room || lk.isConnecting) return
      if (useVoiceStore.getState().currentChannelId !== cid) return
      inFlight = true
      attempts += 1
      try {
        const { audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
        const username = currentUser?.username || currentUser?.id || 'User'
        await useLivekitStore.getState().connect(cid, username, audioInputDeviceId, isNoiseSuppressionOn, { silentIfBlocked: true })
        // room === null means the sticky-ownership gate refused (voice is
        // live on another device). Registering presence then would be a lie.
        if (useLivekitStore.getState().room && !cancelled) {
          getSocket().emit('voice:join', { channelId: cid, userId: currentUser.id })
          participantsFetchStartedAtRef.current = Date.now()
          getSocket().emit('voice:getParticipants', { channelId: cid })
          // NOT `attempts = 0` — a connect that dies seconds later must keep
          // climbing the ladder; check() resets once the room holds STABLE_MS.
          console.info('[VoiceManager] rejoined after an unexpected disconnect', { channelId: cid, attempt: attempts })
        }
      } catch (err) {
        console.warn('[VoiceManager] rejoin attempt failed', { attempt: attempts, err })
      } finally {
        inFlight = false
      }
    }

    const check = () => {
      if (cancelled || inFlight || timer) return
      const cid = useVoiceStore.getState().currentChannelId
      if (!cid) { attempts = 0; roomUpSince = 0; return }
      const lk = useLivekitStore.getState()
      if (lk.room) {
        if (!roomUpSince) roomUpSince = Date.now()
        if (Date.now() - roomUpSince >= STABLE_MS) attempts = 0
        return
      }
      if (lk.isConnecting) return
      roomUpSince = 0
      if (attempts >= 3) return
      const delay = Math.min(2000 * 2 ** attempts, 12000) // 2s, 4s, 8s
      timer = setTimeout(() => { timer = null; void rejoin(cid) }, delay)
    }

    const unsubLk = typeof useLivekitStore.subscribe === 'function' ? useLivekitStore.subscribe(check) : null
    const unsubVoice = typeof useVoiceStore.subscribe === 'function' ? useVoiceStore.subscribe(check) : null
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      if (unsubLk) unsubLk()
      if (unsubVoice) unsubVoice()
    }
  }, [currentUser?.id])

  // Refresh participants list when current channel changes
  useEffect(() => {
    knownParticipantsRef.current.clear()
    if (!currentChannelId) return
    const socket = getSocket()
    const { onParticipants } = handlersRef.current
    if (onParticipants) {
      participantsFetchStartedAtRef.current = Date.now()
      socket.emit('voice:getParticipants', { channelId: currentChannelId })
      socket.on('voice:participants', onParticipants)
      return () => {
        socket.off('voice:participants', onParticipants)
      }
    }
  }, [currentChannelId])

  // Hydrate active poll / presentation run on channel enter or page refresh
  // (server-authoritative — the overlay self-restores after a reload).
  useEffect(() => {
    if (!currentChannelId) {
      useLiveEventStore.getState().reset()
      return
    }
    pollApi.activeRun(currentChannelId)
      .then((res) => {
        const store = useLiveEventStore.getState()
        store.setPoll(res.data?.run ? res.data : null)
        if (res.data?.run && Array.isArray(res.data.myAnswered)) {
          store.setMyVotes(Object.fromEntries(res.data.myAnswered.map((qid: string) => [qid, true])))
        }
      })
      .catch(() => {})
    presentationApi.activeRun(currentChannelId)
      .then((res) => useLiveEventStore.getState().setPresentation(res.data?.run ? res.data : null))
      .catch(() => {})
  }, [currentChannelId])

  // OSPROD-55: LiveKit-driven self-healing reconciler.
  //
  // LiveKit's remoteParticipants is the ground truth for who is actually
  // in the call right now — the SFU only forwards media for participants
  // it has accepted. If LiveKit shows MORE participants than voiceStore
  // has VoiceState rows for in the current channel, the socket-driven
  // path lost a join event (snapshot/delta race, server reconnect blind
  // window, etc) and the sidebar metadata won't render them. The grid
  // also degrades because metadata (avatar, mute, screen-share badge)
  // comes from voiceStore.participants — without a matching row we
  // render a placeholder tile.
  //
  // Note: we compare by COUNT not by identity match. LiveKit `identity`
  // is the participant's username (see livekit token route), while
  // voiceStore keys by DB userId. Identity-level matching would
  // false-positive constantly. A simple count comparison is robust to
  // the username/userId discrepancy and to guests (`guest_*`).
  //
  // Fix: emit a fresh voice:getParticipants and merge — debounced so a
  // burst of joins doesn't fan out N requests. applyParticipantsSnapshot
  // protects any concurrent joins/leaves from being wiped by the
  // late-arriving snapshot.
  useEffect(() => {
    if (!currentChannelId) return
    let pending: ReturnType<typeof setTimeout> | null = null
    const requestRefetch = () => {
      const socket = getSocket()
      const cid = useVoiceStore.getState().currentChannelId
      if (!cid) return
      participantsFetchStartedAtRef.current = Date.now()
      socket.emit('voice:getParticipants', { channelId: cid })
    }
    const check = () => {
      const cid = useVoiceStore.getState().currentChannelId
      if (!cid) return
      const room = useLivekitStore.getState().room
      // Defensive against partial mocks in test (`room.remoteParticipants`
      // can be undefined when a fixture builds a Room-shaped object by
      // hand) and against the brief window between `connect()` resolving
      // and the SFU populating the participants Map.
      const remotes = room && (room as any).remoteParticipants
      if (!remotes || typeof remotes.size !== 'number') return
      // remoteParticipants is everyone EXCEPT the local participant —
      // i.e. the count of "other people" the SFU has joined.
      const livekitOthers: number = remotes.size
      const myId = useAuthStore.getState().user?.id
      const participants = useVoiceStore.getState().participants ?? []
      const voiceStoreOthers = participants.filter(
        (p) => p.channelId === cid && p.userId !== myId
      ).length
      if (livekitOthers > voiceStoreOthers) {
        if (pending) clearTimeout(pending)
        pending = setTimeout(() => { pending = null; requestRefetch() }, 400)
      }
    }
    // Run on every voiceStore.participants OR livekitStore.remoteParticipants
    // change. Both deltas can leave the two views out of sync, so both
    // are watched. `subscribe` may be absent on shallow test mocks of
    // either store — fall back to the once-on-mount check in that case.
    check()
    const unsubVoice = typeof useVoiceStore.subscribe === 'function'
      ? useVoiceStore.subscribe(check)
      : null
    const unsubLk = typeof useLivekitStore.subscribe === 'function'
      ? useLivekitStore.subscribe(check)
      : null
    return () => {
      if (unsubVoice) unsubVoice()
      if (unsubLk) unsubLk()
      if (pending) clearTimeout(pending)
    }
  }, [currentChannelId])

  // Disconnect LiveKit when fully leaving voice
  useEffect(() => {
    if (!currentChannelId) {
      closeSharedAudioContext()
      mediaManager.stopHealthCheck()
      const room = useLivekitStore.getState().room
      if (room) {
        useLivekitStore.getState().disconnect().catch(() => {})
      }
    }
  }, [currentChannelId])

  // Single-instance guard UI: "call active in another tab" block + "use here".
  // Renders only when a user-initiated join was refused (blockedByOtherTab set).
  // Plus the app-wide participant context menu + profile slide-over, mounted
  // once here so any voice tile / participant row can drive them via the store.
  return (
    <>
      <CallTabBlockModal />
      <ParticipantActionsMenu />
      <ParticipantProfileDrawer />
      <RemoteControlController />
    </>
  )
}
